import React, { useRef, useImperativeHandle, forwardRef } from 'react'
import { Swiper, SwiperSlide } from 'swiper/react'
import { Pagination } from 'swiper/modules'
import type { Swiper as SwiperType } from 'swiper'
// @ts-ignore
import 'swiper/css'
// @ts-ignore
import 'swiper/css/pagination'
import CardView from '../card-view'
import './index.css'

// 卡片数据类型（与 API 的 getUserCardListV2CardListItemResp 对齐）
export interface CardData {
  /** 卡可用余额（未下卡为0） */
  available_balance?: number
  /** 余额限额信息（包含真实余额） */
  balance_limit?: {
    balance?: number
    [key: string]: any
  }
  /** 是否已下卡 */
  card_issued?: boolean
  /** 卡产品ID */
  card_product_id?: number
  /** 卡形态 virtual_card/physical_card */
  form_factor?: string
  /** 卡ID（若已下卡）或卡申请ID（若未下卡） */
  item_id?: number | string
  /** 掩码卡号（未下卡为空） */
  masked_pan?: string
  /** 卡状态（已下卡返回卡状态，未下卡返回申请状态） */
  status?: string
  /** 卡片图片 */
  image?: string
  /** 兼容旧字段：卡余额 */
  balance?: number
  /** 兼容旧字段：卡号 */
  pan?: string
  /** 兼容旧字段：卡类型 */
  type?: string
  id?: number | string
  cardNo?: string
  cardType?: 'virtual' | 'physical'
  /** 内部标记：余额是否已通过详情接口更新 */
  _balanceUpdated?: boolean
}

export interface CardSwiperProps {
  /** 卡片列表 */
  cards: CardData[]
  /** 卡片切换回调 */
  onCardChange?: (card: CardData, index: number) => void
  /** 自定义渲染卡片内容 */
  renderCard?: (card: CardData, index: number) => React.ReactNode
  /** 自定义类名 */
  className?: string
  /** 初始索引 */
  initialIndex?: number
  /** 当前激活卡片索引 */
  activeIndex?: number
}

// 暴露给父组件的方法
export interface CardSwiperRef {
  slideTo: (index: number) => void
}

// 默认卡片渲染
const DefaultCard: React.FC<{ card: CardData; index?: number }> = ({ card }) => {
  // 优先级：balance_limit.balance > available_balance > balance
  const displayBalance = card.balance_limit?.balance ?? card.available_balance ?? card.balance
  return (
    <CardView
      cardStatus={card.status}
      cardIssued={card.card_issued === true}
      maskedPan={card.masked_pan}
      balance={displayBalance}
      backgroundImage={card.image}
    />
  )
}

const CardSwiper = forwardRef<CardSwiperRef, CardSwiperProps>(({
  cards,
  onCardChange,
  renderCard,
  className = '',
  initialIndex = 0,
  activeIndex: _activeIndex
}, ref) => {
  const swiperRef = useRef<SwiperType | null>(null)

  // 暴露方法给父组件
  useImperativeHandle(ref, () => ({
    slideTo: (index: number) => {
      swiperRef.current?.slideTo(index)
    }
  }))

  const handleSlideChange = (swiper: SwiperType) => {
    const newIndex = swiper.activeIndex
    if (onCardChange && cards[newIndex]) {
      onCardChange(cards[newIndex], newIndex)
    }
  }

  if (!cards || cards.length === 0) {
    return null
  }

  return (
    <div className={`card-swiper-container ${className}`}>
      <Swiper
        onSwiper={(swiper) => {
          swiperRef.current = swiper
        }}
        initialSlide={initialIndex}
        slidesPerView={1.12}
        centeredSlides={true}
        spaceBetween={12}
        pagination={{
          clickable: true,
          el: '.card-swiper-pagination'
        }}
        modules={[Pagination]}
        onSlideChange={handleSlideChange}
        className="card-swiper"
      >
        {cards.map((card, index) => (
          <SwiperSlide key={card.item_id || card.id || `card-${index}`}>
            <div className="card-item">
              {renderCard ? renderCard(card, index) : <DefaultCard card={card} index={index} />}
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
      
      {/* 自定义指示器 */}
      <div className="card-swiper-pagination"></div>
    </div>
  )
})

CardSwiper.displayName = 'CardSwiper'

export default CardSwiper
